import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPosProviders, type PosProvider } from '@/api/retailPosProviders';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.pos_provider.manage';

const PROVIDER_LABEL: Record<string, string> = {
  checkbox: 'Checkbox',
  vchasno: 'Вчасно',
  mock: 'Демо (mock)',
};

export default function PosProvidersPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-pos-providers', orgId],
    queryFn: () => retailPosProviders.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<PosProvider>[]>(
    () => [
      { key: 'code', header: 'Код', width: 150, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'provider_type',
        header: 'Тип ПРРО',
        width: 160,
        sortValue: (r) => PROVIDER_LABEL[r.provider_type] ?? r.provider_type ?? '',
        cell: (r) => PROVIDER_LABEL[r.provider_type] ?? r.provider_type,
      },
      {
        key: 'is_connected',
        header: 'Підключено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.is_connected ? 1 : 0),
        cell: (r) => (r.is_connected ? '✓' : '—'),
      },
      {
        key: 'last_test_status',
        header: 'Тест зв\'язку',
        width: 160,
        sortValue: (r) => r.last_test_status ?? '',
        cell: (r) => r.last_test_status,
      },
      {
        key: 'is_active',
        header: 'Активний',
        width: 120,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати ПРРО-провайдерами (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          ПРРО-провайдери
          <HintBadge
            title="Що таке ПРРО?"
            text={
              'ПРРО (Програмний Реєстратор Розрахункових Операцій) — це сервіс, ' +
              'який видає фіскальні чеки для ДПС (податкової) замість фізичного касового апарата.\n\n' +
              'Типи провайдерів:\n' +
              '• Checkbox — український комерційний ПРРО (потрібен договір + ліцензія).\n' +
              '• Вчасно — український комерційний ПРРО.\n' +
              '• Mock — тренувальний (для тестування без реального ПРРО). У фазі 1 — використовуйте Mock.\n\n' +
              'Без ПРРО чеки створюються зі статусом «demo» — не вважаються фіскальними. ' +
              'Реальний ПРРО потрібен ДО запуску магазину для клієнтів (по закону про РРО).\n\n' +
              'Кнопка «Тест підключення» перевіряє доступність ПРРО (для Mock — завжди успіх).'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/pos-providers/new')}
        >
          + Новий провайдер
        </button>
      </div>
      <DataTable<PosProvider>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає провайдерів."
        onRowDoubleClick={(r) => navigate(`/retail/pos-providers/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="code"
      />
    </>
  );
}
